import { FunctionComponent, ReactNode, useContext, useEffect, useState } from "react";
import { TransferContext } from "../../../context/TransferContext";
import ScrollableContainer from "../../../layout/scrollable-container";
import Container from "../../../layout/container";

import Flex from "../../../layout/flex";
import TransferStatusTile from "../../../components/transfer-status-tile";
import convertTransferStatus from "../../../utils/convertTransferStatus";
import { NewTransferStatusType, TransferInstruction } from "../../../class/transferInstruction";
import Footer from "../../../components/footer";
import DestinationAccount, { AccountType, DestinationAccountResponse } from "../../../class/destinationAccount";
import NotificationBar from "../../../components/notification-bar";
import { STEP_IDENTITY_VERIFICATION, STEP_SCHEDULE_PHONE_CALL } from "../../../class/transferDetails";
import { HeadlineMedium, Highlight, LabelMedium, TitleMedium } from "../../../components/font";
import { TileContainer, TransferContinueCard } from "./styles";
import { Button } from "@manifesthhqinc/ui-library";
import HeadingContainer from "../../../layout/heading-container";
import { OptionFormContainer } from "../../../layout/container/OptionFormContainer";
import { AppContext } from "../../../context/AppContext";
import { BRIDGE_PRODUCTION } from "../../../typings";
import { MockTask, TransferTask } from "../../../class/transferTask";

const getTransferToString = (data: DestinationAccount | DestinationAccountResponse): string => {
  const isEmployerSponsoredPlan = data.destinationAccountType === AccountType.employerSponsored;
  const isAnnuity = data.destinationAccountType === AccountType.ANNUITY;
  return `${data.providerName ? `${data.providerName} - ` : ""} ${isEmployerSponsoredPlan ? data.planSponsor : isAnnuity ? "Annuity " : "IRA"}`;
};

const HomeScreen: FunctionComponent = () => {
  const { appEnv } = useContext(AppContext);
  const {
    transferDetails,
    userDetails,
    goToScreen,
    setShouldShowHomeScreen,
    transferInstructions,
    clickClose,
    onClickContinueTransfer,
    getTransferProgressText,
    transferTasks,
  } = useContext(TransferContext);
  const [noteBarContent, setNoteBarContent] = useState<ReactNode[]>([]);

  useEffect(() => {
    const content = [];
    // render note bar content logic
    const phoneCallNeeded =
      transferInstructions.filter((s) => s.transferInstructionStatus === NewTransferStatusType.SCHEDULE_PHONE_CALL)
        .length > 0;

    if (phoneCallNeeded && appEnv === BRIDGE_PRODUCTION) {
      content.push(
        <NotificationBar>
          Your provider requires a phone call. Schedule a call with your transfer specialist.{" "}
          <button
            onClick={() => {
              setShouldShowHomeScreen(false);
              goToScreen(STEP_SCHEDULE_PHONE_CALL);
            }}
          >
            Schedule a call
          </button>
        </NotificationBar>,
      );
    }
    const incompleteTransferTasks = transferTasks.filter((task: TransferTask | MockTask) => {
      if ("isMock" in task) {
        return true;
      } else {
        return !task.completedOn;
      }
    });
    incompleteTransferTasks.forEach((task) => {
      if (
        task.type === "IDENTITY_VERIFICATION" &&
        ((task as TransferTask).status === "PENDING" || (task as TransferTask).status === "IN_PROGRESS")
      ) {
        content.push(
          <NotificationBar>
            Verify your identity to continue funding your annuity.{" "}
            <button
              onClick={() => {
                setShouldShowHomeScreen(false);
                goToScreen(STEP_IDENTITY_VERIFICATION);
              }}
            >
              Start verification
            </button>
          </NotificationBar>,
        );
      }
      if (task.type === "SPOUSAL_CONSENT") {
        content.push(<NotificationBar>A notary request has been emailed to your spouse.</NotificationBar>);
      }
    });
    setNoteBarContent(content);
  }, []);

  if (transferInstructions.length > 0) {
    return (
      <>
        <ScrollableContainer name="home-screen">
          <Container>
            {noteBarContent}
            <HeadingContainer paneVariant="list" withProgressBar={false}>
              <HeadlineMedium>
                Hi <Highlight>{userDetails.firstName}</Highlight>, here are your transfers
              </HeadlineMedium>
            </HeadingContainer>
            <OptionFormContainer>
              {transferInstructions.map((transferInstruction: TransferInstruction) => (
                <TransferStatusTile
                  key={transferInstruction.transferInstructionId}
                  status={convertTransferStatus(transferInstruction)}
                  from={transferInstruction.source.planSponsor || ""}
                  to={getTransferToString(transferInstruction.destinations[0])}
                  description={transferInstruction.transferExternalStatusDescription}
                />
              ))}
            </OptionFormContainer>
          </Container>
        </ScrollableContainer>
        <Footer>
          <Button name="exit-manifest" onClick={clickClose} size="large" variant="secondary">
            Exit Manifest
          </Button>
        </Footer>
      </>
    );
  }

  return (
    <>
      <ScrollableContainer name="home-screen">
        <Container>
          <HeadlineMedium>
            Hi <Highlight>{userDetails.firstName}</Highlight>, here's your transfer:
          </HeadlineMedium>
          <TileContainer>
            <TransferContinueCard>
              <Flex $flexDirection="column" $flexGap={8} $alignItems="flex-start">
                <TitleMedium>Continue your transfer</TitleMedium>
                <LabelMedium>
                  <Highlight>Next up</Highlight> | {getTransferProgressText(transferDetails)}
                </LabelMedium>
              </Flex>
              <Button name="Continue" onClick={onClickContinueTransfer}>
                Continue
              </Button>
            </TransferContinueCard>
          </TileContainer>
        </Container>
      </ScrollableContainer>
      <Footer>
        <Button name="exit-manifest" onClick={clickClose} size="large" variant="secondary">
          Exit Manifest
        </Button>
      </Footer>
    </>
  );
};
export default HomeScreen;
